/**
 * useDepartmentMasters.ts
 * TanStack Query data layer for the /masters/departments admin screen.
 * Mirrors the patterns in features/asset/hooks/useAssetCatalog.ts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import epcDepartmentMasterService, {
  type EpcDepartmentMaster,
} from '../../../services/epcDepartmentMasterService';

const KEY = ['epc-department-masters', 'admin'];

export function useDepartmentMasters() {
  const queryClient = useQueryClient();
  // Invalidate the whole prefix so the Team Hierarchy drawer's list refreshes too.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['epc-department-masters'] });

  const query = useQuery({
    queryKey: KEY,
    queryFn: () => epcDepartmentMasterService.list(true),
  });

  const createMutation = useMutation({
    mutationFn: (body: { name: string; focusAreas?: string[] }) => epcDepartmentMasterService.create(body),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof epcDepartmentMasterService.update>[1] }) =>
      epcDepartmentMasterService.update(id, body),
    onSuccess: invalidate,
  });

  const mergeMutation = useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      epcDepartmentMasterService.merge(sourceId, targetId),
    onSuccess: invalidate,
  });

  return { query, createMutation, updateMutation, mergeMutation };
}

export type { EpcDepartmentMaster };
